'use strict'
import React, { useCallback, useMemo } from 'react'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { hasErrorClass } from '../extra/common'
import { useEmployeeCountDispatch, useEmployeeCountSelector } from '../extra/store'
import { ShowErrors } from './ShowErrors'
import { shallowEqual } from 'react-redux'

/*==================================
 定数定義
 ==================================*/
const COUNT_SETTING_OPTIONS = [
  { value: '含める', label: '人数に含める' },
  { value: '含めない', label: '人数に含めない' }
]

/*==================================
 カスタムフック
 ==================================*/
/**
 * 単独出向者の設定に関するロジックを管理するカスタムフック
 *
 * @returns {{
 *   intraGroupSecondmentSource: string
 *   intraGroupSecondmentDestinationLabel: string
 *   interGroupSecondmentSource: string
 *   interGroupSecondmentDestination: string
 *   fiscalYearErrors: object
 *   handleChangeIntraGroupSecondment: (event: { target: { value: string } }) => void
 *   handleChangeInterGroupSecondmentSource: (event: { target: { value: string } }) => void
 *   handleChangeInterGroupSecondmentDestination: (event: { target: { value: string } }) => void
 * }}
 */
const useIndividualSecondmentLogic = () => {
  const dispatch = useEmployeeCountDispatch()

  const { employeeCountSetting, fiscalYearErrors } = useEmployeeCountSelector((state) => {
    const selectedYear = state.employeeCountInfo.selectedFiscalYear
    const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedYear]

    return {
      employeeCountSetting: fiscalYearSettings?.employeeCountSetting?.[0] ?? {},
      fiscalYearErrors: fiscalYearSettings?.errors?.client ?? {}
    }
  }, shallowEqual)

  const {
    intraGroupSecondmentSource = '',
    intraGroupSecondmentDestination = '',
    interGroupSecondmentSource = '',
    interGroupSecondmentDestination = ''
  } = employeeCountSetting

  /** グループ内出向先の表示ラベルを導出 */
  const intraGroupSecondmentDestinationLabel = useMemo(() => {
    return (
      COUNT_SETTING_OPTIONS.find((opt) => opt.value === intraGroupSecondmentDestination)?.label ??
      (intraGroupSecondmentDestination === null ? null : '')
    )
  }, [intraGroupSecondmentDestination])

  /**
   * グループ内出向元の設定変更ハンドラ。 ※出向元と出向先の値を同時に更新
   *
   * @param {(event: { target: { value: string } }) => void} e - イベントオブジェクト => 選択された値 ('含める' or '含めない')
   * @returns {void}
   */
  const handleChangeIntraGroupSecondment = useCallback(
    (e) => {
      const value = e?.target?.value ?? ''
      const oppositeValue = value === '含める' ? '含めない' : '含める'
      dispatch('CHANGE_INTRA_GROUP_SECONDMENT', {
        intraGroupSecondmentSource: value,
        intraGroupSecondmentDestination: oppositeValue
      })
    },
    [dispatch]
  )

  /**
   * グループ外出向元の設定変更ハンドラ
   *
   * @param {(event: { target: { value: string } }) => void} e - イベントオブジェクト => 選択された値 ('含める' or '含めない')
   * @returns {void}
   */
  const handleChangeInterGroupSecondmentSource = useCallback(
    (e) => {
      const value = e?.target?.value ?? ''
      dispatch('CHANGE_INTER_GROUP_SECONDMENT_SOURCE', { interGroupSecondmentSource: value })
    },
    [dispatch]
  )

  /**
   * グループ外出向先の設定変更ハンドラ
   *
   * @param {(event: { target: { value: string } }) => void} e - イベントオブジェクト => 選択された値 ('含める' or '含めない')
   * @returns {void}
   */
  const handleChangeInterGroupSecondmentDestination = useCallback(
    (e) => {
      const value = e?.target?.value ?? ''
      dispatch('CHANGE_INTER_GROUP_SECONDMENT_DESTINATION', { interGroupSecondmentDestination: value })
    },
    [dispatch]
  )

  return {
    intraGroupSecondmentSource,
    intraGroupSecondmentDestinationLabel,
    interGroupSecondmentSource,
    interGroupSecondmentDestination,
    fiscalYearErrors,
    handleChangeIntraGroupSecondment,
    handleChangeInterGroupSecondmentSource,
    handleChangeInterGroupSecondmentDestination
  }
}

/*==================================
 コンポーネント
 ==================================*/
/**
 * 単独出向者の設定セクションをレンダリングするコンポーネント。
 *
 * @returns {React.ReactElement}
 * @component
 */
const IndividualSecondmentSetting = () => {
  const {
    intraGroupSecondmentSource,
    intraGroupSecondmentDestinationLabel,
    interGroupSecondmentSource,
    interGroupSecondmentDestination,
    fiscalYearErrors,
    handleChangeIntraGroupSecondment,
    handleChangeInterGroupSecondmentSource,
    handleChangeInterGroupSecondmentDestination
  } = useIndividualSecondmentLogic()

  const selectClassName = 'max-[400px] w-full max-w-[350px]'

  return (
    <>
      <div className="text-body-2-bold grow mt-6 mb-3">■出向者の設定（単独出向）</div>
      <div className="flex items-center justify-between mb-6 whitespace-pre-line">
        出向先が1社で兼務していない単独出向の従業員を、出向元と出向先のどちらの従業員としてカウントするかを設定します。
        {'\n'}
        本設定は出向情報が登録されている場合のみ適用されます。
      </div>

      <div className="flex gap-3">
        <div className="box-xy-bordered collapse-list flex-1 w-[50%] ">
          <dl className="collapse-node collapse-parent w-full is-open">
            <dt className="flex shrink-0 px-3 pt-3">
              <label htmlFor="intra-group-secondment-source" className="mb-3 text-h6">
                グループ内出向
              </label>
            </dt>
            <dd className="content !overflow-visible">
              <div className="text-h6 font-Regular my-3 mx-5">
                <div className="form-control grid grid-cols-[min-content_1fr] gap-y-4 gap-x-3 items-center font-Regular my-3 whitespace-nowrap ">
                  <div className="">グループ内の他社へ出向中の従業員を</div>
                  <ChrofyCustomSelect
                    id="intra-group-secondment-source"
                    value={intraGroupSecondmentSource}
                    onChange={handleChangeIntraGroupSecondment}
                    className={`${selectClassName} `}
                    options={COUNT_SETTING_OPTIONS}
                    disabled={false}
                    isError={hasErrorClass('intraGroupSecondmentSource', fiscalYearErrors)}
                    hasNoSelect={false}
                  />

                  <div className="">グループ内の他社から出向中の従業員を</div>
                  <div className={`${!!intraGroupSecondmentDestinationLabel && 'ml-4'}`}>
                    {intraGroupSecondmentDestinationLabel}
                    <span className="text-blue-500">（自動設定）</span>
                  </div>
                </div>
                <div className=" items-center justify-between mb-6 text-[#3150a5] font-Regular my-3 mt-5 ">
                  <p>※出向元と出向先で重複カウントを防ぐため、対になる設定は自動設定されます</p>
                </div>
              </div>
            </dd>
          </dl>
        </div>

        <div className="box-xy-bordered collapse-list flex-1 w-[50%]">
          <dl className="collapse-node collapse-parent w-full is-open">
            <dt className="flex shrink-0 px-3 pt-3">
              <label htmlFor="inter-group-secondment-source" className="mb-3 text-h6">
                グループ外出向
              </label>
            </dt>
            <dd className="content !overflow-visible">
              <div className="text-h6 font-Regular my-3 mx-5">
                <div className="form-control grid grid-cols-[min-content_1fr] gap-y-5 gap-x-3 items-center font-Regular my-3 whitespace-nowrap ">
                  <div className=" ">グループ外の他社へ出向中の従業員を</div>
                  <ChrofyCustomSelect
                    id="inter-group-secondment-source"
                    name="interGroupSecondmentSource"
                    value={interGroupSecondmentSource}
                    onChange={handleChangeInterGroupSecondmentSource}
                    className={selectClassName}
                    options={COUNT_SETTING_OPTIONS}
                    disabled={false}
                    isError={hasErrorClass('interGroupSecondmentSource', fiscalYearErrors)}
                    hasNoSelect={false}
                  />

                  <div className=" ">グループ外の他社から出向中の従業員を</div>
                  <ChrofyCustomSelect
                    id="inter-group-secondment-destination"
                    name="interGroupSecondmentDestination"
                    value={interGroupSecondmentDestination}
                    onChange={handleChangeInterGroupSecondmentDestination}
                    className={selectClassName}
                    options={COUNT_SETTING_OPTIONS}
                    disabled={false}
                    isError={hasErrorClass('interGroupSecondmentDestination', fiscalYearErrors)}
                    hasNoSelect={false}
                  />
                </div>
              </div>
            </dd>
          </dl>
        </div>
      </div>

      <ShowErrors clientErrors={fiscalYearErrors} fieldName="individualSecondmentSetting" />
    </>
  )
}

export default IndividualSecondmentSetting
